import React, {useCallback, useMemo} from "react";
import {Modal} from "reactstrap";
import {ErrorMessage, Field, Formik} from "formik";
import * as yup from "yup";

import {
    activationCodeCountError,
    activationCodeFormatError,
    activationCodeMaxTimesError,
    activationCodeMinTimesError,
    activationCodeRequiredError,
    errorMessage,
    requiredError,
    voucherDiscountError
} from "constants/errorMessages";

import {
    MAX_ACTIVATION_CODE_COUNT,
    MAX_ACTIVATION_CODE_LENGTH,
    MAX_ACTIVATION_CODE_TIMES,
    MAX_VOUCHER_DISCOUNT,
    MIN_ACTIVATION_CODE_COUNT,
    MIN_ACTIVATION_CODE_LENGTH,
    MIN_ACTIVATION_CODE_TIMES,
    MIN_VOUCHER_DISCOUNT,
} from "constants/lengthValidity";

import UsageBehaviourInput from "pages/ActivationCode/activationCodeComponents/UsageBehaviourInput";
import ActivationCodeDatePicker from "pages/ActivationCode/activationCodeComponents/activationCodeDatePicker";
import {UploadFileSection} from "pages/ActivationCode/activationCodeComponents/UploadFileSection";
import { lettersAndNumbers } from "constants/validityPatterns";

const schema = yup.object().shape({
    codesCount: yup.number(requiredError)
        .required(requiredError)
        .typeError(requiredError)
        .min(MIN_ACTIVATION_CODE_COUNT, activationCodeCountError)
        .max(MAX_ACTIVATION_CODE_COUNT, activationCodeCountError),

    numberOfUses: yup.number()
        .min(MIN_ACTIVATION_CODE_TIMES, activationCodeCountError)
        .max(MAX_ACTIVATION_CODE_TIMES, activationCodeCountError)
        .when(['isMultiple', 'hasAttachedEmails', 'emails'], {
            is: (isMultiple, hasAttachedEmails, emails) => isMultiple && !hasAttachedEmails && !emails.length,
            then: (schema) => schema.optional(),
            otherwise: (schema) => schema
                .required(requiredError)
                .typeError(requiredError),
        }),

    code: yup.string(requiredError)
        .when("codesCount", {
            is: (value) => parseInt(value) <= MIN_ACTIVATION_CODE_COUNT,
            then: yup.string()
                .required(activationCodeRequiredError)
                .typeError(activationCodeRequiredError)
                .matches(lettersAndNumbers, activationCodeFormatError)
                .min(MIN_ACTIVATION_CODE_LENGTH, activationCodeMinTimesError)
                .max(MAX_ACTIVATION_CODE_LENGTH, activationCodeMaxTimesError)
        }),

    startDate: yup.string(requiredError)
        .required(requiredError)
        .typeError(requiredError)
        .trim(requiredError),

    endDate: yup.string(requiredError)
        .required(requiredError)
        .typeError(requiredError)
        .trim(requiredError),

    isMultiple: yup.boolean().required(),

    discount: yup.number()
        .min(MIN_VOUCHER_DISCOUNT, voucherDiscountError)
        .max(MAX_VOUCHER_DISCOUNT, voucherDiscountError)
        .required(requiredError)
});

const GENERATE = "Generate";
const UPDATE = "Update";
const FIXED_COUNT = "1";

const CreateCouponForm = ({ isOpen, toggle, handleSubmit, initialValues = {}, isEdit = false}) => {

    const initialForm = {
        code: "",
        discount: 10,
        numberOfUses: FIXED_COUNT,
        codesCount: "1",
        startDate: "",
        endDate: "",
        emails: [],
        emailsToDelete: [],
        isMultiple: false,
    }

    const initialState = {
        ...initialForm,
        ...initialValues
    }

    const onSubmit = useCallback((values) => {
        const {
            // eslint-disable-next-line no-unused-vars
            hasAttachedEmails,
            code,
            numberOfUses,
            codesCount,
            emails,
            emailsToDelete,
            ...rest
        } = values;

        handleSubmit({
            ...(Number(codesCount) <= MIN_ACTIVATION_CODE_COUNT && { code }),
            ...(emails.length && { emails }),
            ...(emailsToDelete.length && { emailsToDelete }),
            numberOfUses: numberOfUses || undefined,
            codesCount,
            ...rest
        })
    }, [])

    return (
        <Modal
            isOpen={isOpen}
            centered
        >
            <Formik
                initialValues={{
                    ...initialState
                }}
                onSubmit={(values, { setSubmitting }) => {
                    setSubmitting(true);
                    onSubmit(values);
                    setSubmitting(false);
                }}
                validationSchema={schema}
                enableReinitialize
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    setFieldValue,
                }) => {
                    const disableNumberOfUses = useMemo(() => {
                        const result = parseInt(values.codesCount) > 1;

                        if (result) {
                            setFieldValue("numberOfUses", FIXED_COUNT);
                        }

                        return result;
                    }, [values.codesCount]);

                    const disableNumberOfCodes = useMemo(() => (
                        values?.code.length > 0 || values?.emails?.length
                    ), [values.code, values.emails])

                    return (
                        <form onSubmit={handleSubmit}>
                            <div className="p-40px h-100 w-100">
                                <div>
                                    <label className="font-size-22 font-weight-normal">
                                        {`${isEdit ? UPDATE : GENERATE} voucher code`}
                                    </label>
                                </div>


                                <label className="mt-2">Voucher code body</label>
                                <input
                                    type="text"
                                    name="code"
                                    placeholder="Enter voucher code body"
                                    className="form-control coupon-form-input w-100"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.code}
                                    disabled={disableNumberOfUses}
                                />
                                <ErrorMessage name="code" render={errorMessage}/>

                                <div className="mt-2">
                                    <label className="mb-2" htmlFor="discount">
                                        Discount percentage
                                    </label>
                                    <Field
                                        type="number"
                                        name="discount"
                                        id="discount"
                                        value={values.discount}
                                        className="form-control coupon-form-input"
                                        placeholder="Enter percentage"
                                    />
                                    <div className="position-relative">
                                        <div className="position-absolute">
                                            <ErrorMessage name="discount" render={errorMessage} />
                                        </div>
                                    </div>
                                </div>

                                {(!initialValues?.code || initialValues.hasAttachedEmails) && <UploadFileSection fieldName="emails" isDelete={false}/>}

                                {(isEdit && (!initialValues?.code || initialValues.hasAttachedEmails)) && <UploadFileSection fieldName="emailsToDelete" isDelete={true}/>}

                                <UsageBehaviourInput
                                    fieldName="isMultiple"
                                    value={values.isMultiple}
                                    disabledOnceOption={!!values.emails.length || !!values.hasAttachedEmails}
                                    disabledMultipleOption={disableNumberOfUses}
                                />

                                <div className="mb-3">
                                    <label className="mb-2" htmlFor="numberOfUses">
                                        This code can be used number of times:
                                    </label>
                                    <Field
                                        type="number"
                                        name="numberOfUses"
                                        id="numberOfUses"
                                        className="form-control coupon-form-input"
                                        placeholder="Enter amount"
                                        disabled={disableNumberOfUses}
                                    />
                                    <div className="position-relative">
                                        <div className="position-absolute">
                                            <ErrorMessage name="numberOfUses" render={errorMessage} />
                                        </div>
                                    </div>
                                </div>

                                {!isEdit && (
                                    <div className="mb-2">
                                        <label className="mb-2">The number of codes</label>
                                        <input
                                            type="text"
                                            name="codesCount"
                                            placeholder="Enter the number of codes"
                                            className="form-control coupon-form-input"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.codesCount}
                                            disabled={disableNumberOfCodes || values.isMultiple}
                                        />
                                        <ErrorMessage name="codesCount" render={errorMessage}/>
                                    </div>
                                )}

                                <ActivationCodeDatePicker
                                    errors={errors}
                                    touched={touched}
                                    ComponentStart={() => (<ErrorMessage name="startDate" render={errorMessage}/>)}
                                    ComponentEnd={() => (<ErrorMessage name="endDate" render={errorMessage}/>)}
                                    currentStartDay={values?.["startDate"]}
                                    currentEndDay={values?.["endDate"]}
                                    setFieldValue={setFieldValue}
                                />
                                <section
                                    className="d-flex flex-column flex-sm-row w-100 justify-content-center modal-actions coupon-form-wrapper-buttons">
                                    <button
                                        type="submit"
                                        className="btn btn-primary w-100"
                                    >
                                        {isEdit ? UPDATE : GENERATE}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger w-100"
                                        onClick={toggle}
                                    >
                                        Cancel
                                    </button>
                                </section>
                            </div>
                        </form>
                    )
                }}
            </Formik>
        </Modal>
    )
}

export default CreateCouponForm;
